/* eslint-disable @typescript-eslint/no-use-before-define */
import { useMemo } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { FlexContainer, StyledTooltip } from 'styles/common';
import { check1on1TranscriptionEnabled, checkActionPlanFeatureEnabled } from 'utils/helpers/featureFlags';

import { Chat, Notifications } from 'assets/assetsV1/iconComponents';
import { RabbitEmail } from 'assets/assetsV1/iconComponents/RabbitEmail';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { findAndReplaceString, generateKey } from 'constants/helper';
import { ArrowDownIcon, BrowserNotificationIcon, CheckIcon2, EmptyCheckbox } from 'constants/icons';
import theme from 'constants/theme';

import { getNotificationEventChannelDetails, toggleNotificationEventChannel } from 'services/admin';
import { showSuccessSnackbar } from 'services/snackbar';

import { Typography12, Typography13, Typography14 } from 'uiToolkit/Typography';
import { PaperWithShadow } from 'uiToolkitV1/Paper';

import {
  API_EVENTS_TO_FE_EVENTS_MAPPING,
  NOTIFICATION_EVENTS_META,
  EVENTS_CATEGORY_MAP,
} from '../Notifications/Constants/NotificationSettingsConstants';
import { useNotificationSettings } from './Context/NotificationSettingsContext';

export const NOTIFICATION_EVENT_CHANNELS = {
  BELL: {
    icon: <Notifications boxSize="20px" color={theme.palette.icons.dark} fill="transparent" />,
    type: 'BELL',
    name: 'Bell',
  },
  EMAIL: {
    icon: <RabbitEmail boxSize="20px" color={theme.palette.icons.dark} fill="transparent" />,
    type: 'EMAIL',
    name: 'Email',
  },
  SLACK: {
    icon: <Chat boxSize="22px" color={theme.palette.icons.dark} fill="transparent" />,
    type: 'SLACK',
    name: 'Third party chat applications',
  },
  BROWSER: {
    icon: <BrowserNotificationIcon size="fixed16px" color={theme.palette.icons.dark} />,
    type: 'BROWSER',
    name: 'Browser',
  },
};

const convertEventsFormat = (eventsFromAPI) => {
  let extractedEvents = [];
  eventsFromAPI.forEach((event) => {
    extractedEvents = { ...extractedEvents, ...(event.eventsDetailMap || {}) };
  });
  const moduleToEventMap = {};
  Object.keys(extractedEvents).forEach((eventKey) => {
    if (API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]) {
      const eventDetails = EVENTS_CATEGORY_MAP[API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]];
      // if evenDetails present in FE mapping
      if (eventDetails) {
        if (moduleToEventMap[eventDetails.category]) {
          moduleToEventMap[eventDetails.category].eventsDetailMap[API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]] =
            extractedEvents[eventKey];
        } else {
          moduleToEventMap[eventDetails.category] = {
            moduleType: eventDetails.category,
            moduleOrder: NOTIFICATION_EVENTS_META?.[eventDetails?.category]?.order ?? 0,
            eventsDetailMap: {
              [API_EVENTS_TO_FE_EVENTS_MAPPING[eventKey]]: extractedEvents[eventKey],
            },
          };
        }
      }
    }
  });

  return Object.values(moduleToEventMap).sort(
    (eventModule1, eventModule2) => eventModule1.moduleOrder - eventModule2.moduleOrder
  );
};

const TableHeader = ({ channelDetailsMap = [] }) => (
  <TableHead>
    <TableRow>
      <StyledTableCell width="60%" $borderTop $borderBottom>
        <FlexContainer>
          <Typography14 customColor={theme.palette.text.blackv1}>Notification topics</Typography14>
        </FlexContainer>
      </StyledTableCell>
      {Object.values(channelDetailsMap)?.map(({ channelType }) => {
        const { icon, name } = NOTIFICATION_EVENT_CHANNELS[channelType] ?? {};
        return (
          <StyledTableCell key="type" width="3%" $borderTop $borderBottom>
            <StyledTooltip title={name}>
              <FlexContainer style={{ justifyContent: 'center' }}>{icon}</FlexContainer>
            </StyledTooltip>
          </StyledTableCell>
        );
      })}
    </TableRow>
  </TableHead>
);

const NotificationEventRow = ({
  moduleType,
  mainEventType,
  heading,
  description,
  notificationEventState,
  onToggle,
  channelDetailsMap,
  isNotControlledByNotificationSettings = false,
  message = '',
}) => {
  const { objectivesName, moduleName } = useOrganisationSettings();

  if (!heading) {
    return null;
  }

  const updatedDescription = findAndReplaceString(
    description,
    modifyString(objectivesName, StringModifier.SENTENCE_CASE),
    'objective'
  );

  return (
    <TableRow>
      <StyledTableCell width="60%" $borderBottom>
        <FlexContainer style={{ flexDirection: 'column' }}>
          <Typography13 customColor={theme.palette.text.blackv1}>
            {findAndReplaceString(heading, moduleName, 'Goal')}
          </Typography13>
          <Typography12 customColor={theme.palette.text.label}>
            {findAndReplaceString(updatedDescription, modifyString(moduleName, StringModifier.SENTENCE_CASE), 'goal')}
          </Typography12>
        </FlexContainer>
      </StyledTableCell>
      {isNotControlledByNotificationSettings ? (
        <StyledTableCell colSpan={4} $borderBottom width="3%">
          <FlexContainer style={{ justifyContent: 'center', alignItems: 'center' }}>
            <span>{message}</span>
          </FlexContainer>
        </StyledTableCell>
      ) : (
        <>
          {Object.values(channelDetailsMap).map(({ channelType }) => {
            const { type } = NOTIFICATION_EVENT_CHANNELS[channelType] ?? {};
            if (notificationEventState.NOT_SUPPORTED.includes(type)) {
              return (
                <StyledTableCell $borderBottom key={type} width="3%">
                  <FlexContainer style={{ justifyContent: 'center', alignItems: 'center', padding: '9px' }}>
                    <span style={{ width: '20px', textAlign: 'center' }}>-</span>
                  </FlexContainer>
                </StyledTableCell>
              );
            }
            const isDisabled = !channelDetailsMap[type]?.enabled;
            return (
              <StyledTableCell key={type} width="3%" $borderBottom>
                <FlexContainer style={{ justifyContent: 'center' }}>
                  <StyledCheckBox
                    disableRipple
                    checked={notificationEventState.ENABLED.includes(type)}
                    disabled={isDisabled}
                    color="primary"
                    icon={
                      <EmptyCheckbox
                        size="fixed20px"
                        stroke={isDisabled ? theme.palette.grey.dark50 : 'textSecondary'}
                      />
                    }
                    checkedIcon={
                      <CheckIcon2
                        size="fixed10px"
                        containerSize="fixed20px"
                        smoothEdgeOutline
                        color={isDisabled ? theme.palette.blue.disabledPrimary50 : 'primary'}
                      />
                    }
                    onChange={(e) =>
                      onToggle({
                        moduleType,
                        mainEventType,
                        channelType: type,
                        channelState: e.target.checked ? 'ENABLED' : 'DISABLED',
                      })
                    }
                  />
                </FlexContainer>
              </StyledTableCell>
            );
          })}
        </>
      )}
    </TableRow>
  );
};

const NotificationEventAccordion = ({ type, eventsDetailMap, onToggle, channelDetailsMap }) => {
  const { moduleName } = useOrganisationSettings();

  const eventsDetailWithOrderMap = Object.keys(eventsDetailMap).reduce(
    (previousValue, currentKey) => ({
      ...previousValue,
      [currentKey]: {
        ...eventsDetailMap[currentKey],
        order: NOTIFICATION_EVENTS_META[type][currentKey]?.order ?? 0,
      },
    }),
    {}
  );

  return (
    <StyledAccordion elevation={0} square id={generateKey(5)}>
      <StyledAccordionSummary expandIcon={<ArrowDownIcon size="fixed12px" color="primary" />}>
        {findAndReplaceString(
          NOTIFICATION_EVENTS_META[type].displayName,
          modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
          'Goals'
        )}
      </StyledAccordionSummary>
      <AccordionDetails>
        <Table size="small">
          <TableHeader channelDetailsMap={channelDetailsMap} />
          <TableBody>
            {Object.keys(eventsDetailWithOrderMap)
              .sort((a, b) => (eventsDetailWithOrderMap[a]?.order ?? 0) - (eventsDetailWithOrderMap[b]?.order ?? 0))
              .map((k) => {
                if (
                  (k === 'OOO_COMPLETION_SUMMARY' && !check1on1TranscriptionEnabled()) ||
                  (k === 'REVIEW_ACTION_PLAN' && !checkActionPlanFeatureEnabled())
                ) {
                  return null; // Skip rendering for OOO_COMPLETION_SUMMARY if check1on1TranscriptionEnabled() is false
                }

                const notificationEventState = {
                  ENABLED: eventsDetailMap[k]?.ENABLED,
                  DISABLED: eventsDetailMap[k]?.DISABLED, // of no use really on the web, maybe it's for mobile
                  NOT_SUPPORTED: eventsDetailMap[k]?.NOT_SUPPORTED,
                };
                return (
                  <NotificationEventRow
                    key={k}
                    notificationEventState={notificationEventState}
                    onToggle={onToggle}
                    moduleType={type}
                    mainEventType={k}
                    channelDetailsMap={channelDetailsMap}
                    isNotControlledByNotificationSettings={
                      NOTIFICATION_EVENTS_META[type]?.isNotControlledByNotificationSettings
                    }
                    {...NOTIFICATION_EVENTS_META[type][k]}
                  />
                );
              })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </StyledAccordion>
  );
};

const HeaderComp = ({ isEmployeeNotificationSettings }) => (
  <FlexContainer style={{ flexDirection: 'column' }}>
    <Typography14 weight={600} customColor={theme.palette.text.blackv1}>
      What you get notified about
    </Typography14>
    <Typography13 bottomGutter={3} customColor={theme.palette.text.blackv1}>
      {!isEmployeeNotificationSettings
        ? 'Choose how you’d like your people to be notified across each module'
        : "Choose how you'd like to be notified across each module"}
    </Typography13>
  </FlexContainer>
);

const NotificationEvent = ({ isEmployeeNotificationSettings = false }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    state: { channelEventDetailsArray, loadingChannelDetails, channelDetailsArray },
    setData,
  } = useNotificationSettings();

  const { enabledFeatures = [] } = useSelector(selectLoggedInEmployeeData);

  const fetchData = () => {
    getNotificationEventChannelDetails({ isEmployeeNotificationSettings, enabledFeatures }).then((res) =>
      setData({ ...res, loadingChannelDetails: false })
    );
  };

  const channelDetailsMap = useMemo(
    () => channelDetailsArray.reduce((a, v) => ({ ...a, [v.channelType]: { ...v } }), {}),
    [channelDetailsArray]
  );

  if (loadingChannelDetails) {
    return <Skeleton />;
  }

  const eventChannelOnToggle = ({ moduleType, mainEventType, channelType, channelState }) => {
    const mainEventTypesMap = Object.keys(API_EVENTS_TO_FE_EVENTS_MAPPING);

    const mainEventTypeAPI = mainEventTypesMap.find(
      (apiEventName) => API_EVENTS_TO_FE_EVENTS_MAPPING[apiEventName] === mainEventType
    );
    if (mainEventTypeAPI === undefined) {
      toast({
        title: 'Unknown notification event error',
        status: 'error',
      });
      return;
    }
    toggleNotificationEventChannel({
      moduleType,
      mainEventType,
      channelType,
      channelState,
      isEmployeeNotificationSettings,
    })
      .then(() => {
        fetchData();
        dispatch(showSuccessSnackbar('Notification settings saved successfully!'));
      })
      .catch((err) => {
        console.error('Error in toggling the channel and event : ', err);
      });
  };

  const formattedChannelEventDetailsArray = convertEventsFormat(channelEventDetailsArray);
  return (
    <PaperWithShadow p={4} mt={3}>
      <HeaderComp isEmployeeNotificationSettings={isEmployeeNotificationSettings} />
      {formattedChannelEventDetailsArray.length &&
        formattedChannelEventDetailsArray.map(({ moduleType, eventsDetailMap, featureFlag }) => {
          if (!featureFlag || (featureFlag && featureFlag())) {
            return (
              <NotificationEventAccordion
                key={moduleType}
                type={moduleType}
                eventsDetailMap={eventsDetailMap}
                onToggle={eventChannelOnToggle}
                channelDetailsMap={channelDetailsMap}
                isEmployeeNotificationSettings={isEmployeeNotificationSettings}
              />
            );
          }
          return null;
        })}
    </PaperWithShadow>
  );
};

const StyledAccordion = styled(Accordion)`
  .MuiAccordionSummary-root {
    min-height: 64px;
  }
  &.Mui-expanded {
    margin: auto;
  }
  .MuiAccordionSummary-content.Mui-expanded {
    margin: auto;
  }
  &::before {
    display: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  flex-direction: row-reverse;
  & .MuiAccordionSummary-expandIcon {
    margin-right: 1rem;
    padding: 0;
  }
`;

const StyledTableCell = styled(TableCell)`
  ${(props) =>
    props.$borderTop &&
    css`
      border-top: 1px solid ${props.theme.palette.borders.lighterGrey};
    `}
  ${(props) =>
    props.$borderBottom &&
    css`
      border-bottom: 1px solid ${props.theme.palette.borders.lighterGrey};
    `}
`;

export const StyledCheckBox = styled(Checkbox)``;

export default NotificationEvent;
